import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listPreviews, listReviews } from '@/api/products';
import { Button } from '@/components/Button/Button';
import { QuantityStepper } from '@/components/QuantityStepper/QuantityStepper';
import { Thumb } from '@/components/Thumb/Thumb';
import { WishButton } from '@/components/WishButton/WishButton';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { useAddToCart } from '@/hooks/useAddToCart';
import { useOwnedCopy } from '@/hooks/useOwnedCopy';
import { formatPrice } from '@/utils/format';
import { buyableLanguages, requiresDelivery, unitPrice } from '@/utils/pricing';
import { paths } from '@/routes/paths';
import { NotFound } from '@/pages/NotFound/NotFound';
import styles from './Product.module.css';

export function Product() {
  const { productId } = useParams();
  const { getProduct } = useCatalog();
  const cart = useCart();
  const addToCart = useAddToCart();

  const [qty, setQty] = useState(1);
  const [languages, setLanguages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [previews, setPreviews] = useState([]);
  // The preview currently filling the cover tile, or null for the cover itself.
  const [showing, setShowing] = useState(null);

  const product = getProduct(productId);
  // The reader's copy of this book, in whichever version: a book they own is not
  // sold to them again — they renew it, add a language, or upgrade in playTUB.
  const ownedCopy = useOwnedCopy(product);

  // Read the cart without depending on it: the picker is seeded from the cart
  // line when you arrive at a book, but must not snap back to the stored
  // selection every time the cart changes underneath you mid-edit.
  const linesRef = useRef(cart.lines);
  linesRef.current = cart.lines;

  // Seed the pickers when navigating between products. A book already in the
  // cart opens showing the adaptations it was added with, so ticking one more
  // and pressing the button edits that line instead of quietly dropping the
  // rest. A language chosen for one book still means nothing for the next.
  useEffect(() => {
    setQty(1);
    const line = linesRef.current.find((l) => String(l.id) === String(productId));
    setLanguages(line?.languages ?? []);
  }, [productId]);

  const toggleLanguage = (language) =>
    setLanguages((prev) =>
      prev.includes(language) ? prev.filter((l) => l !== language) : [...prev, language],
    );

  useEffect(() => {
    // Cleared here rather than in the .then, so navigating between books never
    // shows one book's chapters under another book's cover while the fetch runs.
    setPreviews([]);
    setShowing(null);

    const controller = new AbortController();
    listPreviews(productId, { signal: controller.signal })
      .then(setPreviews)
      .catch(() => setPreviews([]));
    return () => controller.abort();
  }, [productId]);

  // Four of the book's chapters, drawn at random so the samples on offer differ
  // between visits, then put back into reading order. Recomputed only when the
  // fetch lands, so nothing reshuffles under a reader who is clicking through them.
  const sample = useMemo(() => pickPreviews(previews, PREVIEW_COUNT), [previews]);

  // Escape backs out of a preview, the same as the close button — this is a
  // lightbox in all but name, and that is the key people reach for.
  useEffect(() => {
    if (!showing) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowing(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showing]);

  useEffect(() => {
    const controller = new AbortController();
    listReviews(productId, { signal: controller.signal })
      .then(setReviews)
      .catch(() => setReviews([]));
    return () => controller.abort();
  }, [productId]);

  if (!product) return <NotFound />;

  // Digital books are an entitlement on a playTUB account, so there is no
  // second copy to buy — the picker is only meaningful for printed stock.
  const canPickQuantity = requiresDelivery(product);

  // One line per book, so this button either creates it or edits it.
  const inCart = cart.lines.some((l) => String(l.id) === String(product.id));

  return (
    <div className={styles.page}>
      <div className={styles.crumbs}>
        <Link to={paths.home}>Home</Link> /{' '}
        <Link to={paths.category(product.categoryId)}>{product.categoryName}</Link> /{' '}
        {product.name}
      </div>

      <div className={styles.layout}>
        <div className={styles.media}>
          {/* Wraps the tile itself, so the overlays sit on the cover rather
              than in the column around it — Thumb's "lg" is capped at 480px
              and centred, which on a wide screen is narrower than this column. */}
          <div className={styles.cover}>
            <Thumb
              letter={product.letter}
              tint={product.tint}
              deep={product.deep}
              src={showing ? showing.image_url : product.image_url}
              alt={showing ? previewLabel(showing, product) : `Cover of ${product.name}`}
              fit="contain"
              size="lg"
            />

            {/* The badge belongs to the cover — leaving "Free" stuck over a
                page of the book would read as a label on that content. */}
            {product.tag && !showing && <div className={styles.tag}>{product.tag}</div>}

            {showing && (
              <button
                type="button"
                className={styles.previewClose}
                onClick={() => setShowing(null)}
                aria-label="Back to the cover"
              >
                ×
              </button>
            )}
          </div>

          <PreviewStrip
            previews={sample}
            showing={showing}
            product={product}
            onShow={setShowing}
          />
        </div>

        <div>
          <div className={styles.eyebrow}>{product.categoryName}</div>
          <h1 className={styles.title}>{product.name}</h1>

          {product.hasRating ? (
            <div className={styles.rating}>
              <span aria-hidden="true">{product.stars}</span>{' '}
              <span className={styles.ratingMeta}>
                {product.rating} · {product.ratingCount} ratings
              </span>
            </div>
          ) : (
            product.author && <div className={styles.byline}>by {product.author}</div>
          )}

          <div className={styles.price}>
            {product.pricing?.is_free
              ? 'Free'
              : formatPrice(unitPrice(product, languages), product.currency)}
          </div>
          <p className={styles.desc}>{product.desc}</p>

          {ownedCopy ? (
            <OwnedCopy product={product} entry={ownedCopy} />
          ) : (
            <LanguagePicker
              product={product}
              selected={languages}
              onToggle={toggleLanguage}
              onClear={() => setLanguages([])}
            />
          )}

          <BookDetails product={product} />

          <div className={styles.buyRow} hidden={Boolean(ownedCopy)}>
            {canPickQuantity && (
              <QuantityStepper
                value={qty}
                onIncrement={() => setQty((q) => q + 1)}
                onDecrement={() => setQty((q) => Math.max(1, q - 1))}
              />
            )}

            <Button
              size="lg"
              className={styles.addBtn}
              onClick={() => {
                addToCart(product, canPickQuantity ? qty : 1, languages);
                cart.open();
              }}
            >
              {inCart ? 'Update cart' : 'Add to cart'}
            </Button>

            <WishButton productId={product.id} productName={product.name} size="lg" />
          </div>

          {/* Printed copies aren't on sale yet, so there is no delivery to
              describe — only where a digital edition actually turns up. */}
          {!requiresDelivery(product) && (
            <div className={styles.shipNote}>
              📖 Digital edition — opens in the playTUB app on the account you buy it with.
              Nothing to ship, nothing to wait for.
            </div>
          )}

          <section className={styles.reviews}>
            <h2 className={styles.reviewsTitle}>What people say</h2>
            <ul className={styles.reviewList}>
              {reviews.map((r) => (
                <li key={r.id} className={styles.review}>
                  <div className={styles.reviewHead}>
                    <span className={styles.reviewWho}>{r.who}</span>
                    <span className={styles.reviewStars} aria-hidden="true">
                      {r.stars}
                    </span>
                  </div>
                  <div className={styles.reviewText}>{r.text}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * A book the reader already owns: what they hold, and what they can do with it.
 *
 * Never a second copy — the platform keeps one purchase per reader and book,
 * whichever version. Instead:
 *   - a newer version than theirs is a free upgrade, done in the playTUB app;
 *   - a language they don't have yet can be added for the REST of their term (the
 *     expiry doesn't move), at its price times the share of the term left — the
 *     platform's own fraction, so the preview is what checkout will charge;
 *   - late in the term (or once it has ended) the language comes with a renewal.
 */
function OwnedCopy({ product, entry }) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState([]);

  const held = entry.additional_languages ?? [];
  const options = (product.pricing?.language_options ?? []).filter(
    (o) => !o.is_base && o.available && !held.includes(o.language),
  );
  const fraction = Number(entry.addon_term_fraction) || 0;
  const canAdd = Boolean(entry.addon_window_open);
  const partTerm = (price) => Math.round(price * fraction * 100) / 100;
  const cost = options
    .filter((o) => adding.includes(o.language))
    .reduce((sum, o) => sum + partTerm(o.price), 0);
  const until = ownedDay(entry.expiry_date);
  const behind = entry.book_id !== product.id;

  const toggle = (language) =>
    setAdding((prev) => (prev.includes(language) ? prev.filter((l) => l !== language) : [...prev, language]));

  return (
    <section className={styles.owned} aria-label="Your copy">
      <div className={styles.ownedTitle}>In your library</div>
      <p className={styles.ownedText}>
        You own this book in {[product.language, ...held].filter(Boolean).join(', ')}
        {until && (entry.is_expired ? ` · expired on ${until}` : ` · until ${until}`)}.
      </p>
      {behind && (
        <p className={styles.ownedNote}>
          You have version {entry.version_number}. Version {product.version_number} is out —
          upgrade to it for free in the playTUB app.
        </p>
      )}

      {options.length > 0 && (entry.renewable || canAdd) && (
        <fieldset className={styles.editions}>
          <legend className={styles.editionsTitle}>
            Add a language
            <span className={styles.editionsHint}>
              {canAdd ? ' — for the rest of your term' : ' — when you renew'}
            </span>
          </legend>
          <ul className={styles.editionList}>
            {options.map((opt) => (
              <li key={opt.language} className={styles.edition}>
                <label className={styles.editionLabel}>
                  <input
                    type="checkbox"
                    className={styles.editionCheck}
                    checked={adding.includes(opt.language)}
                    onChange={() => toggle(opt.language)}
                  />
                  <span className={styles.editionName}>{opt.language}</span>
                </label>
                <span className={styles.editionPrice}>
                  +{formatPrice(canAdd ? partTerm(opt.price) : opt.price, product.currency)}
                </span>
              </li>
            ))}
          </ul>
        </fieldset>
      )}

      <div className={styles.ownedActions}>
        {canAdd && adding.length > 0 && (
          <Button size="lg" onClick={() => navigate(paths.addLanguage(product.id, adding))}>
            Add {adding.length === 1 ? adding[0] : `${adding.length} languages`} ·{' '}
            {formatPrice(cost, product.currency)}
          </Button>
        )}
        {entry.renewable && (
          <Button
            size="lg"
            variant={canAdd && adding.length > 0 ? 'ghost' : 'primary'}
            onClick={() => navigate(paths.renew(product.id, canAdd ? [] : adding))}
          >
            {!canAdd && adding.length > 0 ? `Renew with ${adding.join(', ')}` : 'Renew'}
          </Button>
        )}
      </div>
      {canAdd && options.length > 0 && (
        <p className={styles.ownedHint}>
          A language added now lasts until your copy&apos;s term ends. Discount codes don&apos;t
          apply to it.
        </p>
      )}
    </section>
  );
}

/** "20 October 2026" from an ISO timestamp, or '' when there is none. */
function ownedDay(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** How many chapter samples sit under the cover. */
const PREVIEW_COUNT = 4;

/**
 * A few of the book's chapter images, chosen at random and left in reading order.
 *
 * Random rather than the first few: chapter one is the least representative
 * page of most books, and a shopper who comes back sees something new. Sorting
 * afterwards means the strip still reads front-to-back, and chapters with no
 * number (the source allows it) sink to the end instead of jumping the queue.
 */
function pickPreviews(previews, count) {
  const pool = [...previews];
  const picked = [];
  while (pool.length > 0 && picked.length < count) {
    picked.push(...pool.splice(Math.floor(Math.random() * pool.length), 1));
  }
  return picked.sort((a, b) => (a.chapter_number ?? Infinity) - (b.chapter_number ?? Infinity));
}

/** What the cover tile is showing while a preview fills it. */
function previewLabel(preview, product) {
  return preview.chapter_name
    ? `${product.name}: ${preview.chapter_name}`
    : `A page from ${product.name}`;
}

/** What the thumbnail button is called. Chapters can share a name, so the
 *  position in the strip disambiguates rather than leaving two identical
 *  controls for anyone navigating by label. */
function previewButtonLabel(preview, position) {
  return preview.chapter_name
    ? `Preview ${position}: ${preview.chapter_name}`
    : `Preview ${position}`;
}

/**
 * Sample content from inside the book, under its cover.
 *
 * Clicking one shows it in the cover tile rather than opening a modal: it is
 * the one place on the page already sized for a picture, so nothing below it
 * moves and the buy controls stay put. Renders nothing for a book whose
 * chapters have no generated images — most of the catalog has them, and an
 * empty "Inside this book" heading would promise something that isn't there.
 */
function PreviewStrip({ previews, showing, product, onShow }) {
  if (previews.length === 0) return null;

  return (
    <div className={styles.previews}>
      <div className={styles.previewsTitle}>Inside this book</div>
      <ul className={styles.previewList}>
        {previews.map((preview, i) => {
          const active = showing?.image_url === preview.image_url;
          return (
            <li key={preview.image_url}>
              <button
                type="button"
                className={`${styles.previewBtn} ${active ? styles.previewActive : ''}`}
                style={{ background: product.tint }}
                onClick={() => onShow(active ? null : preview)}
                aria-pressed={active}
                aria-label={previewButtonLabel(preview, i + 1)}
                title={preview.chapter_name ?? undefined}
              >
                {/* Decorative: the button carries the name, and a second copy
                    on the image would read it out twice. */}
                <img
                  className={styles.previewImg}
                  src={preview.image_url}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * The book record behind a listing: who made it, who it's for, and what each
 * language edition costs. Renders nothing for products without book fields, so
 * non-book catalog items are unaffected.
 */
function BookDetails({ product }) {
  const { publisher } = product;

  const rows = [
    ['Format', product.format === 'printed' ? 'Printed copy' : 'Digital · reads in playTUB'],
    ['Priced in', product.currency],
    ['Author', product.author],
    ['Publisher', publisher?.name],
    ['Subject', product.subject],
    ['Board', product.education_board],
    ['Age group', product.grade_level],
    ['Language', product.language],
    ['Contents', contentsLabel(product)],
  ].filter(([, value]) => Boolean(value));

  if (rows.length === 0) return null;

  return (
    <>
      <dl className={styles.specs}>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.specRow}>
            <dt className={styles.specKey}>{label}</dt>
            <dd className={styles.specValue}>{value}</dd>
          </div>
        ))}
      </dl>

      {publisher?.website && (
        <div className={styles.publisherNote}>
          {[publisher.city, publisher.country].filter(Boolean).join(', ')}
          {publisher.city && ' · '}
          {publisher.website}
        </div>
      )}

      {product.tags?.length > 0 && (
        <ul className={styles.tags}>
          {product.tags.map((t) => (
            <li key={t} className={styles.tagChip}>
              {t}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/**
 * Choose which language editions to buy.
 *
 * The base language is always included and can't be deselected — it's the
 * headline price. Each additional language adds its own price on top, and
 * translations the publisher hasn't put on sale are shown but not selectable,
 * so the catalog stays honest about what exists.
 */
function LanguagePicker({ product, selected, onToggle, onClear }) {
  const options = product.pricing?.language_options ?? [];
  if (options.length === 0) return null;

  const isFree = Boolean(product.pricing?.is_free);
  const buyable = buyableLanguages(product);
  const extrasTotal = unitPrice(product, selected) - (product.price ?? 0);

  return (
    <fieldset className={styles.editions}>
      <legend className={styles.editionsTitle}>
        Languages
        {buyable.length > 0 && !isFree && (
          <span className={styles.editionsHint}>
            {' '}
            — add additional language adaptations to your copy
          </span>
        )}
      </legend>

      {selected.length > 0 && (
        <div className={styles.editionsSummary}>
          <span>
            {selected.length} added
            {extrasTotal > 0 && ` · +${formatPrice(extrasTotal, product.currency)}`}
          </span>
          <button type="button" className={styles.editionsClear} onClick={onClear}>
            Clear
          </button>
        </div>
      )}

      <ul className={styles.editionList}>
        {options.map((opt) => {
          const checked = opt.is_base || selected.includes(opt.language);
          const selectable = !opt.is_base && opt.available;

          return (
            <li key={opt.language} className={styles.edition}>
              <label
                className={`${styles.editionLabel} ${selectable ? '' : styles.editionFixed}`}
              >
                <input
                  type="checkbox"
                  className={styles.editionCheck}
                  checked={checked}
                  disabled={!selectable}
                  onChange={() => onToggle(opt.language)}
                />
                <span className={styles.editionName}>{opt.language}</span>
                {opt.is_base && <span className={styles.editionBadge}>included</span>}
                {!opt.available && <span className={styles.editionBadge}>not for sale</span>}
              </label>

              {/* Only the base language is "Included" — it is in the copy
                  whatever you do. An adaptation that costs nothing is still
                  something you have to add, so it is priced, not declared
                  included, until its box is ticked. */}
              <span className={styles.editionPrice}>
                {!opt.available
                  ? '—'
                  : opt.is_base
                    ? 'Included'
                    : opt.price === 0
                      ? 'Free to add'
                      : `+${formatPrice(opt.price, product.currency)}`}
              </span>
            </li>
          );
        })}
      </ul>

      {product.pricing?.subscription_monthly > 0 && product.pricing?.subscription_period_months && (
        <div className={styles.subNote}>
          Also available monthly:{' '}
          {formatPrice(product.pricing.subscription_monthly, product.currency)} a month for{' '}
          {product.pricing.subscription_period_months}{' '}
          {product.pricing.subscription_period_months === 1 ? 'month' : 'months'}.
        </div>
      )}
    </fieldset>
  );
}

function contentsLabel(product) {
  const parts = [
    product.chapter_count && `${product.chapter_count} chapters`,
    product.page_count && `${product.page_count} pages`,
    product.image_count && `${product.image_count} images`,
  ].filter(Boolean);
  return parts.join(' · ');
}
